import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { ThunderboltOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { apiErrorCode, apiErrorMessage } from '@/api/client';
import { listPayees, type Payee } from '@/api/payees';
import {
  listApplications,
  createApplication,
  createAiApplication,
  executePayout,
  rejectApplication,
  type ApplicationFilter,
  type ApplicationStatus,
  type AiApplicationResult,
  type FeePayer,
  type PayoutApplication,
  type SwiftChargeOption,
} from '@/api/payouts';
import {
  APPLICATION_STATUS_COLOR,
  APPLICATION_STATUS_LABEL,
  DEFAULT_APPLICATION_REFERENCE,
  FEE_PAYER_LABEL,
  SWIFT_CHARGE_LABEL,
  buildApplicationPayload,
  isSwiftPayee,
} from '@/domain/payouts';
import { useSessionStore } from '@/store/session';
import AmountWithCNY, { CnyHint, ConversionHint } from '@/components/common/AmountWithCNY';
import AmountRangeFilter from '@/components/common/AmountRangeFilter';
import FilterBar from '@/components/common/FilterBar';
import { ReceiptPreviewModal } from '@/components/common/ReceiptPreviewModal';
import PayoutDetailDrawer from '@/components/payouts/PayoutDetailDrawer';
import AiApplicationModal from '@/components/payouts/AiApplicationModal';
import { detailFromApplication } from '@/domain/payoutDetail';
import { useListFilter } from '@/hooks/useListFilter';

const { Text } = Typography;

const STATUS_OPTIONS = (
  Object.keys(APPLICATION_STATUS_LABEL) as ApplicationStatus[]
).map((value) => ({ value, label: APPLICATION_STATUS_LABEL[value] }));

/**
 * 「新建申请」表单里的手续费选择。承担方现在只在创建申请时选一次、随后定死
 * （见 PRODUCT.md §5.8），打款确认弹窗不再用这个组件，只读展示当时的选择。
 *
 * 🔴 两个开关都预选「我们承担」：这是改动前的实际行为，保持它意味着不会有人
 * 因为这次改动而在不知情的情况下把费用转给了达人。
 *
 * 放在组件外层（而不是 `PayoutApplications` 内部定义）：父组件每次渲染都会
 * 重新求值 JSX，如果 `FeeChoice` 是内部函数，每次渲染都是新的组件类型，
 * React 会把它当成整个卸载重装，用户已经选好的单选框会被重置。
 */
function FeeChoice({
  swift,
  initial,
  onChange,
}: {
  swift: boolean;
  initial?: { feePaidBy: FeePayer; swiftChargeOption?: SwiftChargeOption };
  onChange: (v: { feePaidBy: FeePayer; swiftChargeOption?: SwiftChargeOption }) => void;
}) {
  const [feePaidBy, setFeePaidBy] = useState<FeePayer>(initial?.feePaidBy ?? 'PAYER');
  const [swiftOption, setSwiftOption] = useState<SwiftChargeOption>(
    initial?.swiftChargeOption ?? 'PAYER',
  );

  useEffect(() => {
    // 🔴 非 SWIFT 收款人时，swiftChargeOption 这个键必须彻底不出现在传给
    // executePayout 的参数里（而不是传值为 undefined 的键）——后端对非 SWIFT
    // 收款人传这个字段会 400（SWIFT_OPTION_NOT_APPLICABLE）。这里直接按 swift
    // 分支构造两种不同形状的对象，而不是用三元表达式塞一个可能是 undefined 的值，
    // 后者会让这个键「存在但是 undefined」，仍然会被当成传了。
    onChange(swift ? { feePaidBy, swiftChargeOption: swiftOption } : { feePaidBy });
  }, [feePaidBy, swiftOption, swift, onChange]);

  return (
    <Space direction="vertical" size={8} style={{ marginTop: 12, width: '100%' }}>
      <div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Airwallex 手续费
        </Text>
        <Radio.Group
          size="small"
          style={{ display: 'block', marginTop: 4 }}
          value={feePaidBy}
          onChange={(e) => setFeePaidBy(e.target.value as FeePayer)}
          options={(['PAYER', 'BENEFICIARY'] as FeePayer[]).map((v) => ({
            value: v,
            label: FEE_PAYER_LABEL[v],
          }))}
          optionType="button"
        />
      </div>
      {swift && (
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            SWIFT 中转行费用
          </Text>
          <Radio.Group
            size="small"
            style={{ display: 'block', marginTop: 4 }}
            value={swiftOption}
            onChange={(e) => setSwiftOption(e.target.value as SwiftChargeOption)}
            options={(['PAYER', 'SHARED'] as SwiftChargeOption[]).map((v) => ({
              value: v,
              label: SWIFT_CHARGE_LABEL[v],
            }))}
            optionType="button"
          />
        </div>
      )}
      {feePaidBy === 'BENEFICIARY' && (
        <Text type="warning" style={{ fontSize: 12 }}>
          对方实际到手将少于此金额。
        </Text>
      )}
    </Space>
  );
}

/**
 * 打款申请。admin / finance 可提，member 一律 403；只有**财务**能在这一页按下「打款」。
 *
 * 🔴 打款按钮的交互是刻意做重的：二次确认里要把金额、币种、收款人原样再念一遍。
 * 这是一个不可撤销、且只能执行一次的动作，不该和「删除一行」用同样轻的交互。
 */
export default function PayoutApplications() {
  const { message, modal } = App.useApp();
  const me = useSessionStore((s) => s.me);
  const canPay = me?.roles.includes('finance') ?? false;

  const [rows, setRows] = useState<PayoutApplication[]>([]);
  const [currencies, setCurrencies] = useState<string[]>([]);
  const [payeeOptions, setPayeeOptions] = useState<Payee[]>([]);
  const f = useListFilter<ApplicationFilter>();
  // 身份稳定，供 useCallback 依赖（每次渲染都新建的话，下游那个防抖会被反复重建）
  const { setMany } = f;
  /** 「清空筛选」时连带把金额输入框也抹掉 —— 它自己存着一份本地值。 */
  const [resetKey, setResetKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [aiCreating, setAiCreating] = useState(false);
  /** AI 流程不会选已有收款人；这里暂存待与申请原子创建的新 SWIFT 收款人。 */
  const [aiDraft, setAiDraft] = useState<AiApplicationResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  /** 正在打款的申请 id。按 id 记而不是一个全局 boolean，避免一行 loading 把整列按钮锁死。 */
  const [paying, setPaying] = useState<string | null>(null);
  const [notConfigured, setNotConfigured] = useState(false);
  /** 正在看哪一条申请的详情。null = 抽屉关着。 */
  const [detailRow, setDetailRow] = useState<PayoutApplication | null>(null);
  /** 抽屉里那一段收款人信息。跟着 detailRow 异步拉，拉不到就在抽屉里明说。 */
  const [detailPayee, setDetailPayee] = useState<Payee | null>(null);
  const [detailPayeeLoading, setDetailPayeeLoading] = useState(false);
  const [detailPayeeError, setDetailPayeeError] = useState<string | null>(null);
  /** 连着快点两行时，只认最后点的那一条 —— 否则先发的那次请求回来会把内容盖回去。 */
  const detailSeq = useRef(0);
  /** 抽屉里点开的转账确认函。null = 预览弹窗关着。 */
  const [receiptOf, setReceiptOf] = useState<PayoutApplication | null>(null);
  const [form] = Form.useForm<{
    payeeId?: string;
    amount: string;
    reference: string;
    note?: string;
  }>();
  // 金额框要跟着选中的收款人显示币种、跟着输入显示参考换算，所以这两个值得订阅
  const watchedPayeeId = Form.useWatch('payeeId', form);
  const watchedAmount = Form.useWatch('amount', form);
  /** 🔴 币种只能来自收款人的银行账户，见 PRODUCT.md §5.1。这里只是把它显示出来。 */
  const selectedPayee = payeeOptions.find((p) => p.id === watchedPayeeId) ?? null;
  const selectedCurrency = aiDraft?.currency ?? selectedPayee?.accountCurrency ?? null;
  /** 收款人在这张表单里是可改的，SWIFT 那组开关跟着当前选中的收款人动态出现/消失。 */
  const createSwift = aiDraft !== null || isSwiftPayee(selectedPayee?.transferMethod ?? null);
  /**
   * 新建申请表单里的承担方选择。这张表单是普通的受控 `<Form>`，整个 `<Modal>`
   * 都是正常渲染的 JSX，`submit` 每次渲染都会拿到最新闭包，用 state 即可，也不用
   * 操心手动 delete 键——`setFeeChoice` 整个对象替换掉旧值，`FeeChoice` 的
   * `swift` 变化时也会通过它自己的 effect 重新调用一次 onChange，不会有残留的
   * `swiftChargeOption`。
   */
  const [feeChoice, setFeeChoice] = useState<{
    feePaidBy: FeePayer;
    swiftChargeOption?: SwiftChargeOption;
  }>({ feePaidBy: 'PAYER' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { items, facets } = await listApplications(f.filter);
      setRows(items);
      setCurrencies(facets.currencies);
    } catch (err) {
      message.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [message, f.filter]);

  /**
   * 收款人列表，用于「新建申请」的下拉和这一页的详情抽屉。拉过一次就复用。
   *
   * 🔴 详情**取自本地镜像**，与收款人页的抽屉同一个来源 —— 同一个收款人在两处
   * 看到的东西必须一模一样。不走 `/payees/get`：那个接口会拿远端结果覆盖本地镜像，
   * 而远端回空时它会把币种、账户名一起清掉（见 payees.service.ts 的 getPayee），
   * 一个只读的详情抽屉不该有能力改坏数据。
   */
  const ensurePayees = useCallback(async (): Promise<Payee[]> => {
    if (payeeOptions.length > 0) return payeeOptions;
    const { items } = await listPayees();
    setPayeeOptions(items);
    return items;
  }, [payeeOptions]);

  /**
   * 打开这条申请的详情。
   *
   * 抽屉**立刻**打开：这一行的申请信息（金额、附言、备注、手续费决定、后来打出去没有）
   * 已经在手上了，没有任何理由让人对着一个转圈的按钮等收款人拉回来。收款人那一段
   * 单独 loading，拉不到就在抽屉里明说「没取到」——不能留白让人误以为这个人没填。
   */
  function openDetail(row: PayoutApplication) {
    const seq = ++detailSeq.current;
    setDetailRow(row);
    setDetailPayee(null);
    setDetailPayeeError(null);
    setDetailPayeeLoading(true);
    void (async () => {
      try {
        const found = (await ensurePayees()).find((x) => x.id === row.payeeId);
        if (seq !== detailSeq.current) return;
        if (!found) {
          setDetailPayeeError('找不到这个收款人，请刷新后重试');
          return;
        }
        setDetailPayee(found);
      } catch (err) {
        if (seq !== detailSeq.current) return;
        setDetailPayeeError(apiErrorMessage(err));
      } finally {
        if (seq === detailSeq.current) setDetailPayeeLoading(false);
      }
    })();
  }

  function resetFilter() {
    f.reset();
    setResetKey((k) => k + 1);
  }

  /** 金额区间：🔴 两端都是字符串，原样交给接口，全程不进 Number。 */
  const onAmountRange = useCallback(
    (amountMin?: string, amountMax?: string) => setMany({ amountMin, amountMax }),
    [setMany],
  );

  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    void load();
  }, [load]);

  async function openCreate() {
    try {
      const list = await ensurePayees();
      setNotConfigured(false);
      if (list.length === 0) {
        message.warning('还没有收款人，请先到「收款人」页添加');
        return;
      }
      setAiDraft(null);
      form.resetFields();
      setCreating(true);
    } catch (err) {
      if (apiErrorCode(err) === 'AIRWALLEX_NOT_CONFIGURED') setNotConfigured(true);
      else message.error(apiErrorMessage(err));
    }
  }

  /**
   * AI 录入是与「批量导入 / 新建申请」并列的入口，并会创建一个全新的 SWIFT 收款人，
   * 所以这里不拉、不匹配现有收款人。
   */
  function openAiCreate() {
    setAiDraft(null);
    setAiCreating(true);
  }

  async function submit() {
    let values: { payeeId?: string; amount: string; reference: string; note?: string };
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSubmitting(true);
    try {
      if (aiDraft) {
        const created = await createAiApplication({
          sourceRecordId: aiDraft.sourceRecordId,
          payeeValues: aiDraft.payee.values,
          amount: values.amount.trim(),
          reference: values.reference.trim(),
          note: values.note?.trim() || undefined,
          feePaidBy: feeChoice.feePaidBy,
          ...(feeChoice.swiftChargeOption
            ? { swiftChargeOption: feeChoice.swiftChargeOption }
            : {}),
        });
        setPayeeOptions((current) => [...current, created.payee]);
        message.success('新 SWIFT 收款人与申请已创建');
      } else {
        await createApplication(
          buildApplicationPayload(
            {
              payeeId: values.payeeId!,
              // 🔴 原样传字符串。绝不 Number(values.amount)。
              amount: values.amount.trim(),
              reference: values.reference.trim(),
              note: values.note?.trim() || undefined,
            },
            createSwift,
            feeChoice,
          ),
        );
        message.success('申请已提交');
      }
      setCreating(false);
      setAiDraft(null);
      form.resetFields();
      setFeeChoice({ feePaidBy: 'PAYER' });
      void load();
    } catch (err) {
      message.error(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  /** AI 结果里带的是一个全新 SWIFT 收款人；下一步提交时与申请在同一事务创建。 */
  function applyAiApplication(result: AiApplicationResult) {
    form.resetFields();
    form.setFieldsValue({
      amount: result.values.amount,
      reference: result.values.reference ?? DEFAULT_APPLICATION_REFERENCE,
      note: result.values.note,
    });
    setAiDraft(result);
    setFeeChoice({
      feePaidBy: result.values.feePaidBy,
      ...(result.values.swiftChargeOption
        ? { swiftChargeOption: result.values.swiftChargeOption }
        : {}),
    });
    setAiCreating(false);
    setCreating(true);
    message.success('AI 结果已填入；提交后会同时创建新 SWIFT 收款人与申请');
  }

  /**
   * 执行打款。
   *
   * 🔴 失败时的措辞极其重要：`PAYOUT_RESULT_UNKNOWN` 意味着钱**可能已经出去了**，
   * 界面绝不能说「打款失败」——那会诱导财务再建一条申请重打。
   */
  function confirmPay(row: PayoutApplication) {
    modal.confirm({
      title: '确认打款',
      width: 460,
      content: (
        <Space direction="vertical" size={4} style={{ marginTop: 8 }}>
          <Text>
            收款人：<Text strong>{row.payeeName ?? '—'}</Text>
          </Text>
          <div>
            <Text>金额：</Text>
            <Text strong style={{ fontSize: 18 }}>
              {row.amount} {row.currency}
            </Text>
            {/* 确认打款是最该看清金额的一屏，人民币参考值也要在这里给出。
                用 CnyHint 而不是 AmountWithCNY —— 上面已经把金额放大显示过了，
                再用后者会把金额重复渲染一遍。 */}
            <div style={{ marginTop: 2 }}>
              <CnyHint amount={row.amount} currency={row.currency} />
            </div>
          </div>
          <Text type="secondary">附言：{row.reference}</Text>
          {/*
            🔴 只读展示，不可改。承担方在申请创建时已经定死（见 PRODUCT.md §5.8、
            domain/payouts.ts 顶部注释），这里给的是「即将按什么生效」的确认信息，
            不是给财务临时改主意的地方——executePayout 也确实不接受这个参数，选了
            也不会生效，所以干脆不给选。
          */}
          <Text type="secondary">
            手续费：{FEE_PAYER_LABEL[row.feePaidBy]}
            {row.swiftChargeOption
              ? ` · SWIFT 中转行 ${SWIFT_CHARGE_LABEL[row.swiftChargeOption as SwiftChargeOption]}`
              : ''}
          </Text>
          <Alert
            style={{ marginTop: 12 }}
            type="warning"
            showIcon
            title="这笔申请只能打款一次"
            description="无论成功或失败都不会再次打款。请确认金额与收款人无误。"
          />
        </Space>
      ),
      okText: '确认打款',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        setPaying(row.id);
        try {
          await executePayout(row.id);
          message.success('已发起打款，结果以打款记录为准');
        } catch (err) {
          if (apiErrorCode(err) === 'PAYOUT_RESULT_UNKNOWN') {
            modal.warning({
              title: '打款结果待确认',
              content:
                '请求已发出但未收到确认，系统会自动核对结果。请勿重复操作——这笔钱可能已经在路上。',
            });
          } else if (apiErrorCode(err) === 'PAYOUT_EXCEEDS_MAX_AMOUNT') {
            /*
             * 超过单笔上限。用 Modal 而不是一闪而过的 message：这笔钱**没有打出去**，
             * 而财务需要明确知道是被规则挡下的、不是失败了——否则他会去重试。
             * 服务端的文案里带着本次金额、币种和上限，原样显示，别在这里另写一句
             * 更模糊的话把它盖掉。
             */
            modal.error({
              title: '超过最大金额限制',
              content: apiErrorMessage(err),
            });
          } else {
            message.error(apiErrorMessage(err));
          }
        } finally {
          setPaying(null);
          void load();
        }
      },
    });
  }

  /**
   * 拒绝。🔴 理由必填 —— 申请人只能重提一条新的，不知道理由就只能猜该改什么。
   * 所以用带输入框的 Modal，不是 Popconfirm（Popconfirm 装不下一个必填的多行输入）。
   */
  function confirmReject(row: PayoutApplication) {
    let reason = '';
    const instance = modal.confirm({
      title: '拒绝这条申请',
      okText: '确认拒绝',
      okButtonProps: { danger: true },
      content: (
        <Space direction="vertical" size={8} style={{ marginTop: 8, width: '100%' }}>
          <Text type="secondary">
            {row.payeeName ?? '—'} · {row.amount} {row.currency}
          </Text>
          <Input.TextArea
            rows={3}
            maxLength={1000}
            placeholder="必填。写清楚为什么不付，申请人据此重提。"
            onChange={(e) => {
              reason = e.target.value;
            }}
          />
        </Space>
      ),
      onOk: async () => {
        if (!reason.trim()) {
          message.error('请填写拒绝理由');
          throw new Error('reason required');
        }
        await rejectApplication(row.id, reason.trim());
        message.success('已拒绝');
        void load();
      },
    });
    // 理由为空时 onOk 主动 throw，这是让 antd 的确认弹窗保持打开、不把「校验没过」
    // 误关成「已处理」的唯一方式。但 antd 只有在调用方订阅了这个返回值的 `.then`
    // 时才会把那次 reject 当成「调用方自己会处理」而不再往外抛——不订阅的话，
    // 每次理由为空都会在控制台冒出一条无害但吵人的 unhandled rejection。
    void instance.then(
      () => {},
      () => {},
    );
  }

  if (notConfigured) {
    return (
      <Card title="打款申请">
        <Alert
          type="warning"
          showIcon
          title="本组织还没有配置 Airwallex 凭据"
          description="配置之后才能添加收款人与发起打款。"
        />
      </Card>
    );
  }

  return (
    <Card
      title="打款申请"
      extra={
        <Space>
          {/* 批量导入是「新建申请」的一个变体（一次建多条），不是独立业务链路——
              所以放在这里而不是侧栏，且刻意排在「新建申请」左边、次要位置。 */}
          <Link to="/payout-batch-import">
            <Button>批量导入</Button>
          </Link>
          <Link to="/payout-applications/ai-batch">
            <Button icon={<ThunderboltOutlined />}>AI 批量创建申请</Button>
          </Link>
          <Button icon={<ThunderboltOutlined />} onClick={openAiCreate}>
            AI 录入
          </Button>
          <Button type="primary" onClick={openCreate}>
            新建申请
          </Button>
        </Space>
      }
    >
      <FilterBar active={f.active} resultCount={rows.length} onReset={resetFilter}>
        <Input
          allowClear
          style={{ width: 220 }}
          placeholder="搜收款人 / 短 id / 附言 / 备注"
          value={f.keyword}
          onChange={(e) => f.setKeyword(e.target.value)}
        />
        <Select
          mode="multiple"
          allowClear
          style={{ minWidth: 150 }}
          placeholder="状态"
          options={STATUS_OPTIONS}
          value={f.filter.status ?? []}
          onChange={(v: ApplicationStatus[]) => f.set('status', v)}
        />
        <Select
          mode="multiple"
          allowClear
          style={{ minWidth: 120 }}
          placeholder="币种"
          options={currencies.map((c) => ({ value: c, label: c }))}
          value={f.filter.currency ?? []}
          onChange={(v: string[]) => f.set('currency', v)}
        />
        <AmountRangeFilter onChange={onAmountRange} resetKey={resetKey} />
        <DatePicker.RangePicker
          placeholder={['申请起', '申请止']}
          value={f.range}
          onChange={f.setRange}
        />
      </FilterBar>

      <Table<PayoutApplication>
        rowKey="id"
        loading={loading}
        dataSource={rows}
        pagination={false}
        // 列比较多，窄屏下让表格自己横向滚动，而不是把每一列挤成一列单字
        scroll={{ x: 'max-content' }}
        columns={[
          {
            // 短 id 并进这一列而不是单开一列：这张表已经八列了，而短 id 与姓名、别名
            // 说的是同一件事 ——「这是谁」。拆开只会让人横着把它们拼回去。
            title: '收款人',
            width: 200,
            render: (_, row) => (
              <Space direction="vertical" size={0}>
                {/* 🔴 只有姓名可点，不是整行可点。这一行最右边就是不可撤销的「打款」，
                    它的点击区域不能和任何别的东西重叠（收款人页那里整行可点是因为
                    那一行本身就是一个收款人，而且没有会动钱的按钮）。 */}
                <Tooltip title="查看这条申请与收款人的完整详情">
                  <Button
                    type="link"
                    size="small"
                    onClick={() => openDetail(row)}
                    style={{ padding: 0, height: 'auto', fontWeight: 600 }}
                  >
                    {row.payeeName ?? '—'}
                  </Button>
                </Tooltip>
                <Space size={6}>
                  {/* 不许断行：短 id 被折成两行就没法一眼抄下来了 */}
                  <Text code style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                    {row.payeeShortId}
                  </Text>
                  {row.payeeNickname && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {row.payeeNickname}
                    </Text>
                  )}
                </Space>
              </Space>
            ),
          },
          {
            title: '金额',
            width: 170,
            align: 'right',
            render: (_, row) => <AmountWithCNY amount={row.amount} currency={row.currency} />,
          },
          { title: '附言', dataIndex: 'reference', width: 150 },
          {
            title: '状态',
            dataIndex: 'status',
            width: 160,
            render: (s: PayoutApplication['status'], row) => (
              <Space direction="vertical" size={2}>
                <Tag color={APPLICATION_STATUS_COLOR[s]}>{APPLICATION_STATUS_LABEL[s]}</Tag>
                {/* 已拒绝的行在标签下方标出理由与拒绝人；理由可能很长，用 antd 自带的
                    Typography 截断 + 悬浮显示全文，不新写一套截断 CSS。 */}
                {row.status === 'REJECTED' && row.rejectionReason ? (
                  <Text
                    type="secondary"
                    style={{ fontSize: 12, maxWidth: 160, display: 'block' }}
                    ellipsis={{ tooltip: row.rejectionReason }}
                  >
                    {row.rejectedByName ? `${row.rejectedByName}：` : ''}
                    {row.rejectionReason}
                  </Text>
                ) : null}
              </Space>
            ),
          },
          { title: '申请人', dataIndex: 'createdByName', width: 100 },
          {
            title: '申请时间',
            dataIndex: 'createdAt',
            width: 160,
            render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm'),
          },
          {
            title: '操作',
            width: 190,
            render: (_, row) => {
              if (row.status !== 'PENDING') {
                return (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {row.status === 'PROCESSING' ? '打款中' : '—'}
                  </Text>
                );
              }
              // 「拒绝」与「打款」共用同一个 canPay 判据——服务端已经独立挡住了
              // 非财务角色，这里的 disabled 只是不在界面上留一个必然 403 的入口。
              return (
                <Space size={4}>
                  <Tooltip title={canPay ? undefined : '只有财务角色可以打款'}>
                    <Button
                      danger
                      type="primary"
                      size="small"
                      disabled={!canPay}
                      loading={paying === row.id}
                      onClick={() => confirmPay(row)}
                    >
                      打款
                    </Button>
                  </Tooltip>
                  <Tooltip title={canPay ? undefined : '只有财务角色可以拒绝'}>
                    <Button
                      type="link"
                      size="small"
                      disabled={!canPay}
                      onClick={() => confirmReject(row)}
                    >
                      拒绝
                    </Button>
                  </Tooltip>
                </Space>
              );
            },
          },
        ]}
      />

      {/*
        与打款记录页共用同一个抽屉：一笔钱的申请侧与执行侧在两页看到的必须是同一份东西。
        🔴 抽屉里没有任何动钱的按钮 —— 打款仍然只能从上面那一行最右边按下去。
      */}
      <PayoutDetailDrawer
        detail={detailRow ? detailFromApplication(detailRow) : null}
        payee={detailPayee}
        payeeLoading={detailPayeeLoading}
        payeeError={detailPayeeError}
        onClose={() => {
          // 关抽屉时把序号推进一格：还在路上的那次收款人请求回来后不再写状态
          detailSeq.current += 1;
          setDetailRow(null);
          setDetailPayee(null);
          setDetailPayeeLoading(false);
        }}
        onViewReceipt={() => setReceiptOf(detailRow)}
      />

      {/* 确认函的页内预览，与打款记录页同一个组件 */}
      <ReceiptPreviewModal
        key={receiptOf?.payout?.id ?? 'none'}
        payoutId={receiptOf?.payout?.id ?? null}
        subtitle={
          receiptOf ? `${receiptOf.payeeName ?? '—'} · ${receiptOf.amount} ${receiptOf.currency}` : undefined
        }
        onClose={() => setReceiptOf(null)}
      />

      <Modal
        open={creating}
        title={aiDraft ? '新建 SWIFT 收款人与打款申请' : '新建打款申请'}
        okText={aiDraft ? '创建收款人并提交申请' : '提交申请'}
        confirmLoading={submitting}
        onCancel={() => {
          setCreating(false);
          setAiCreating(false);
          setAiDraft(null);
          form.resetFields();
          // 下一次打开这张表单不该带着这一次已经关掉的选择。
          setFeeChoice({ feePaidBy: 'PAYER' });
        }}
        onOk={submit}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" initialValues={{ reference: DEFAULT_APPLICATION_REFERENCE }}>
          {aiDraft ? (
            <Form.Item label="新收款人">
              <Alert
                type="info"
                showIcon
                title={aiDraft.payee.values['beneficiary.bank_details.account_name'] || '新 SWIFT 收款人'}
                description={`将创建新的 SWIFT 收款人 · ${aiDraft.currency ?? '币种待确认'}；不会关联已有收款人。`}
              />
            </Form.Item>
          ) : (
            <Form.Item name="payeeId" label="收款人" rules={[{ required: true, message: '请选择收款人' }]}>
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="选择收款人"
                options={payeeOptions.map((p) => ({
                  value: p.id,
                  label: `${p.accountName ?? '—'}${p.nickname ? ` · ${p.nickname}` : ''} · ${p.accountCurrency ?? ''}`,
                }))}
              />
            </Form.Item>
          )}
          <Form.Item
            name="amount"
            label="金额"
            rules={[
              { required: true, message: '请输入金额' },
              {
                // 与服务端 money.ts 同一套规则。最多两位小数的正数。
                pattern: /^(0|[1-9]\d{0,17})(\.\d{1,2})?$/,
                message: '最多两位小数的正数',
              },
              {
                validator: (_, v: string) =>
                  !v || Number(v) > 0 ? Promise.resolve() : Promise.reject(new Error('金额必须大于 0')),
              },
            ]}
            extra={
              <Space direction="vertical" size={2} style={{ marginTop: 4 }}>
                {/* 🔴 输入的那一刻就给出美元 / 人民币参考值：这是「多打一个零」最容易
                    被自己发现的时机，比打款前的二次确认早一步。 */}
                <ConversionHint amount={watchedAmount ?? ''} currency={selectedCurrency} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  币种由收款人的银行账户决定，不需要另选。
                </Text>
              </Space>
            }
          >
            <Input
              inputMode="decimal"
              placeholder="例如 1200.50"
              // 币种紧跟在金额后面：申请人不需要回头去看自己选的是哪个收款人
              addonAfter={selectedCurrency ?? '—'}
            />
          </Form.Item>
          <Form.Item
            name="reference"
            label="银行附言"
            rules={[{ required: true, message: '请输入附言' }, { max: 128 }]}
            extra="默认 KOL Marketing，会展示给收款人；需要时可以直接修改。"
          >
            <Input />
          </Form.Item>
          <Form.Item name="note" label="内部备注" rules={[{ max: 1000 }]}>
            <Input.TextArea rows={2} placeholder="仅内部可见，不发给收款人" />
          </Form.Item>
          <FeeChoice
            key={aiDraft ? 'ai-new-swift-payee' : 'existing-payee'}
            swift={createSwift}
            initial={
              aiDraft
                ? {
                    feePaidBy: aiDraft.values.feePaidBy,
                    swiftChargeOption: aiDraft.values.swiftChargeOption,
                  }
                : undefined
            }
            onChange={setFeeChoice}
          />
        </Form>
      </Modal>

      <AiApplicationModal
        open={aiCreating}
        onClose={() => setAiCreating(false)}
        onApply={applyAiApplication}
      />
    </Card>
  );
}
